import React, { useState } from 'react';

import Axios from 'axios';
import { Formik, Field } from 'formik';
import * as Yup from 'yup';

import { forceReloadContact } from '@hooks/API/Contact/UseContact';
import { fetchCustomersByArea } from '@hooks/API/Contact/UseContactsList';
import useConfigurations from '@hooks/Configurations/UseConfigurations';
import useFormFieldFocus from '@hooks/Utils/UseFormFieldFocus';

import { NwCancelButton, NwSaveButton } from '@components/Gui/NwButton';
import NwDrawer from '@components/Gui/NwDrawer';
import { NWInputNumber, NWSwitch } from '@components/Gui/NwForm/NwFormItems';
import { NwForm, NwFormButtonsBar } from '@components/Gui/NwForm/NwFormWrapper';

const MotherAgencyCommissionDrawer = ({ networkAgency, customer, onSetCustomer, onClose, onReloadSearch }) => {
    useFormFieldFocus('Commission');
    const [touchForm, setTouchForm] = useState(false);

    const configuration = useConfigurations();
    const configurationCommissionOnGross = configuration.defaultMotherAgencyCommissionPercOnGross;
    const configurationCommission = configuration.defaultMotherAgencyCommissionPerc;

    const defaultCommission =
        networkAgency.MotherAgencyCommission != null ? networkAgency.MotherAgencyCommission : configurationCommission;
    const defaultCommissionOnGross =
        networkAgency.MotherAgencyCommission != null && networkAgency.MotherAgencyCommissionOnGross != null
            ? networkAgency.MotherAgencyCommissionOnGross
            : configurationCommissionOnGross;

    const initialValues = {
        Commission: defaultCommission,
        CommissionOnGross: defaultCommissionOnGross,
    };

    const saveNetworkAgencyCommission = async (values, setSubmitting) => {
        setSubmitting(true);
        try {
            await Axios.post('/customers/setMotherAgencyCommission', {
                ContactID: networkAgency.ContactID,
                NetworkAgencyID: networkAgency.NetworkAgencyID,
                Commission: parseFloat(values.Commission).toFixed(2),
                CommissionOnGross: values.CommissionOnGross,
            });
            if (customer.NWAGS && customer.NWAGS.length > 0) {
                const networkAgencyIndex = customer.NWAGS.findIndex((agency) => agency.ID === networkAgency.ID);
                let updatedCustomer = { ...customer };
                updatedCustomer.NWAGS[networkAgencyIndex].MotherAgencyCommission = parseFloat(
                    values.Commission
                ).toFixed(2);
                updatedCustomer.NWAGS[networkAgencyIndex].MotherAgencyCommissionOnGross = values.CommissionOnGross;
                onSetCustomer(updatedCustomer);
                fetchCustomersByArea();
            } else {
                forceReloadContact(customer.ID);
            }
            if (onReloadSearch) {
                onReloadSearch();
            }
            setSubmitting(false);
            onClose();
        } catch (error) {
            console.error('Update Network Agency Fee Error => ', error);
            setSubmitting(false);
        }
    };

    return (
        <NwDrawer
            touchForm={touchForm}
            onTouchForm={setTouchForm}
            title={'Edit Mother Agency Default Commission'}
            onClose={onClose}
        >
            <Formik
                initialValues={initialValues}
                validationSchema={Yup.object().shape({
                    Commission: Yup.number()
                        .typeError('Commission must be a number')
                        .required('Commission is required, add zero for no commission')
                        .min(0, 'Please input correct percentage, it should be between 0 and 99.99')
                        .max(99.99, 'Please input correct percentage, it should be between 0 and 99.99')
                        .test('max-two-decimals', 'Please input only two decimals', (value) => {
                            if (value === undefined || value === null) {
                                return true;
                            }
                            return /^\d+(\.\d{1,2})?$/.test(String(value));
                        }),
                })}
                onSubmit={(values, { setSubmitting }) => {
                    setSubmitting(true);
                    saveNetworkAgencyCommission(values, setSubmitting);
                }}
            >
                {({ isSubmitting, isValid, values, handleSubmit }) => (
                    <NwForm values={values} onTouchForm={setTouchForm} onFinish={handleSubmit} layout="vertical">
                        <Field
                            label="Custom Mother Agency Commission Percentage"
                            name="Commission"
                            component={NWInputNumber}
                            style={{ width: '6rem' }}
                            min={0}
                            step={0.1}
                            value={values.Commission}
                        />
                        <Field
                            component={NWSwitch}
                            label="On Gross"
                            name="CommissionOnGross"
                            checked={Boolean(values.CommissionOnGross)}
                        />
                        <NwFormButtonsBar
                            style={{ marginTop: '2rem' }}
                            left={
                                <NwCancelButton
                                    disabled={isSubmitting}
                                    onClick={() => {
                                        setTouchForm(false);
                                        onClose();
                                    }}
                                />
                            }
                            right={
                                <NwSaveButton
                                    htmlType="submit"
                                    disabled={!isValid || isSubmitting}
                                    loading={isSubmitting}
                                />
                            }
                        />
                    </NwForm>
                )}
            </Formik>
        </NwDrawer>
    );
};

export default MotherAgencyCommissionDrawer;
